import React, { useState, useEffect, useRef } from 'react';
import { searchLocations } from '../utils/geocodingService';
import { fallbackCitiesData } from '../utils/cityData';

const LocationAutocomplete = ({ onSelect, initialValue, className = "" }) => {
    const [inputValue, setInputValue] = useState(initialValue || '');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [usingFallback, setUsingFallback] = useState(false);
    const wrapperRef = useRef(null);
    const searchTimeoutRef = useRef(null);

    // Set initial city if provided
    useEffect(() => {
        if (initialValue && !inputValue) {
            setInputValue(initialValue);
        }
    }, [initialValue]);

    // Search with geocoding API and fallback to static data
    const getSuggestions = async (input) => {
        if (!input || input.length < 2) {
            setSuggestions([]);
            setUsingFallback(false);
            return;
        }

        setIsSearching(true);
        setUsingFallback(false);

        try {
            // First try the geocoding API
            const apiResults = await searchLocations(input);

            if (apiResults && apiResults.length > 0) {
                setSuggestions(apiResults);
                setUsingFallback(false);
            } else {
                // Fallback to static data if API returns no results
                const inputLower = input.toLowerCase();
                const fallbackResults = fallbackCitiesData
                    .filter(city => city.name.toLowerCase().includes(inputLower))
                    .slice(0, 5);

                setSuggestions(fallbackResults);
                setUsingFallback(fallbackResults.length > 0);
            }
        } catch (error) {
            console.error('Location search error:', error);

            // Fallback to static data on error
            const inputLower = input.toLowerCase();
            const fallbackResults = fallbackCitiesData
                .filter(city => city.name.toLowerCase().includes(inputLower))
                .slice(0, 5);

            setSuggestions(fallbackResults);
            setUsingFallback(true);
        } finally {
            setIsSearching(false);
        }
    };

    // Debounced search to avoid too many API calls
    const debouncedSearch = (input) => {
        // Clear existing timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Set new timeout
        searchTimeoutRef.current = setTimeout(() => {
            getSuggestions(input);
        }, 500); // 500ms delay
    };

    // Handle input change with debouncing
    const handleInputChange = (e) => {
        const value = e.target.value;
        setInputValue(value);

        if (value.length > 1) {
            setShowSuggestions(true);
            setIsSearching(true);
            debouncedSearch(value);
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
            setIsSearching(false);
            setUsingFallback(false);

            // Clear timeout if input is too short
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        }
    };

    // Handle suggestion selection
    const handleSuggestionClick = (location) => {
        setInputValue(location.name);
        setSuggestions([]);
        setShowSuggestions(false);
        setUsingFallback(false);

        // Pass coordinates to parent component in "lat,lng" format
        onSelect(`${location.lat},${location.lng}`, location.name);
    };

    // Clear the input
    const handleClearInput = () => {
        setInputValue('');
        setSuggestions([]);
        setShowSuggestions(false);
        setIsSearching(false);
        setUsingFallback(false);

        // Clear any pending search
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Clear the selected location in parent component
        onSelect('', '');
    };

    // Close suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [wrapperRef]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    return (
        <div className="relative w-full" ref={wrapperRef}>
            <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" className="text-#A961ED" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                </div>

                <input
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    placeholder="Enter a city name"
                    onFocus={() => inputValue.length > 1 && setShowSuggestions(true)}
                    className={`w-full py-2 pl-8 pr-8 rounded-lg bg-transparent border border-white focus:border-accent focus:outline-none text-[14px] font-light mb-0 ${className}`}
                />

                <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                    {isSearching && (
                        <div className="text-accent mr-2 animate-spin">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                        </div>
                    )}

                    {inputValue && (
                        <button
                            onClick={handleClearInput}
                            className="text-gray-400 hover:text-white transition-colors focus:outline-none"
                            aria-label="Clear input"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                </div>
            </div>

            {showSuggestions && (
                <div className="absolute mt-1 w-full rounded-lg bg-gray-900 border border-gray-700 shadow-lg overflow-hidden z-10">
                    {usingFallback && suggestions.length > 0 && (
                        <div className="px-3 py-1 text-xs text-yellow-400 bg-gray-800 border-b border-gray-700">
                            Showing popular cities (geocoding unavailable)
                        </div>
                    )}
                    <ul className="max-h-48 py-1 overflow-auto text-sm">
                        {suggestions.length > 0 ? (
                            suggestions.map((location, index) => (
                                <li
                                    key={index}
                                    onClick={() => handleSuggestionClick(location)}
                                    className="px-3 py-1.5 flex items-center hover:bg-gray-800 cursor-pointer transition-colors"
                                    title={location.displayName || location.name}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" className="text-#A961ED mr-1.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    <span className="text-white text-sm">{location.name}</span>
                                </li>
                            ))
                        ) : (
                            <li className="px-3 py-2 text-gray-400 text-center text-sm">
                                {isSearching ? 'Searching...' : 'No locations found. Try a different search.'}
                            </li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default LocationAutocomplete; 